'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const moments = [
{
  title: 'Anondo Mela',
  subtitle: 'Shashthi Evening · Friday, 16 October',
  bengali: 'আনন্দ মেলা',
  description: 'Housewives cook at home and sell their food to the gathering - a real community kitchen, not a catering contract. A warm, bustling home-style food bazaar that opens the festival. Authentic Bengali cuisine, sweets, street food, and cultural stalls.',
  image: "/assets/images/anondo_mela.png",
  alt: 'Community food bazaar at Anondo Mela with guests being served home-cooked Bengali food',
  highlights: ['Authentic home-cooked Bengali food', 'Community kitchen by local families', 'Cultural and artisan stalls', 'Open across all 6 days'],
  icon: '🍛'
},
{
  title: 'Sindoor Khela',
  subtitle: 'Dashami · Wednesday, 21 October',
  bengali: 'সিঁদুর খেলা',
  description: 'Women from every walk of life come together in a sea of colour. Two hours of live dhaak and ritual colour play - a powerful, deeply photogenic crescendo that closes the festival. One of the most shared, most anticipated moments of the Puja.',
  image: "/assets/images/sindoor_khela.png",
  alt: 'Women celebrating with vibrant red sindoor powder in bright natural light, joyful expressions and colorful traditional clothing',
  highlights: ['Live dhaak drumming', 'Sindoor Khela', 'Women from all communities', 'Closing ceremony of Prathaa 2026'],
  icon: '🌺'
}];


export default function SignatureMomentsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {entries.forEach((e) => {if (e.isIntersecting) e.target.classList.add('active');});},
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14 reveal">
          <span className="inline-block px-4 py-1.5 rounded-full border border-border text-muted-foreground text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
            Signature Moments
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight">
            Moments You <span className="text-primary">Won&apos;t Forget</span>
          </h2>
        </div>

        <div className="flex flex-col gap-10 md:gap-12">
          {moments?.map((moment, i) =>
          <div
            key={moment?.title}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center ${i % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
            
              {/* Image */}
              <div className={`relative overflow-hidden rounded-3xl md:rounded-4xl h-64 md:h-80 lg:h-96 group card-depth ${i % 2 === 1 ? 'lg:col-start-2 reveal-right' : 'reveal-left'}`}>
                <AppImage
                src={moment?.image}
                alt={moment?.alt}
                fill
                className="object-cover grayscale-hover"
                sizes="(max-width: 1024px) 100vw, 50vw" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/60 to-transparent" />
                <div className="absolute bottom-4 left-4 md:bottom-5 md:left-5">
                  <span className="text-2xl md:text-3xl">{moment?.icon}</span>
                </div>
              </div>

              {/* Content */}
              <div className={i % 2 === 1 ? 'lg:col-start-1 lg:row-start-1 reveal-left' : 'reveal-right'}>
                <div className="mb-2">
                  <span className="text-muted-foreground text-xs font-bold tracking-widest uppercase">{moment?.subtitle}</span>
                </div>
                <div className="flex flex-wrap items-baseline gap-3 md:gap-4 mb-3 md:mb-4">
                  <h3 className="font-display text-3xl md:text-4xl font-black text-foreground tracking-tight">{moment?.title}</h3>
                  <span className="text-accent/70 text-lg md:text-xl" style={{ fontFamily: "'Noto Serif Bengali', serif" }}>{moment?.bengali}</span>
                </div>
                <p className="text-muted-foreground text-sm md:text-base leading-relaxed mb-5 md:mb-6">{moment?.description}</p>
                <ul className="space-y-2 md:space-y-2.5">
                  {moment?.highlights?.map((h) =>
                <li key={h} className="flex items-center gap-3 text-sm text-foreground/80">
                      <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <svg className="text-primary" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      </div>
                      {h}
                    </li>
                )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}